import { useEffect, useState } from "react";
import { SpaceBetween } from "@cloudscape-design/components-themed";
import {
  CorporateNumberSearchUI,
  checkCorporateNumber,
  registerCheckCorporateNumberErrorMsg,
  registerCorporateData,
} from "./CorporateNumberSearch";
import {
  AddressUI,
  checkPostalCode,
  registerCheckPostalCodeErrorMsg,
  registerAddressData,
} from "./AddressForm";
import {
  CorporateNumberInfo,
  RegisterAddressInfo,
} from "../../../features/api-interfaces";
import {
  useGetAddressByPostalCodeQuery,
  useGetCorpInfoByCorpNumQuery,
} from "../../../features/api";

export const AddressFormUI = (props: any) => {
  // 所在地情報
  const [addressInfoData, setAddressInfoData] = useState({
    postalCode: "",
    address1: "",
    address1Kn: "",
    address2: "",
    address2Kn: "",
    prfCd: "",
    ctyCd: "",
    lrgCd: "",
    chrCd: "",
    phoneNumber: "",
  } as RegisterAddressInfo);

  //法人情報
  const [corporateData, setCorporateData] = useState({
    corporateNumber: "",
    companyName: "",
    companyNameKana: "",
  } as CorporateNumberInfo);

  //法人番号検索条件を初期化する
  const [searchInput, setSearchInput] = useState("");

  //郵便番号検索条件を初期化する
  const [postalCodeSearchInput, setPostalCodeSearchInput] = useState("");

  //事業者情報取得API
  const { data } = useGetCorpInfoByCorpNumQuery(searchInput, {
    skip: !searchInput,
  });

  //住所取得API
  const { data: postalCodeData } = useGetAddressByPostalCodeQuery(
    postalCodeSearchInput,
    {
      skip: !postalCodeSearchInput,
    }
  );

  //新規の場合、法人番号押下後取得してきた事業者情報をセット
  useEffect(() => {
    if (data?.companyInfo) {
      setCorporateData({
        ...corporateData,
        corporateNumber: registerCorporateData.corporateNumber,
        companyName: data.companyInfo.companyName,
        companyNameKana: data.companyInfo.companyNameKana,
      });
      setIsCorporateNumberSearchClicked(true);
    }
    //検索条件を初期化する
    setSearchInput("");
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [data]);

  //新規の場合、法人番号押下後取得してきた住所データをセット
  useEffect(() => {
    if (postalCodeData?.address) {
      let address1 =
        postalCodeData.address?.prfNm +
        postalCodeData.address?.ctyOrCnt +
        postalCodeData.address?.lrgLtt;
      let address1Kn =
        postalCodeData.address?.prfNmKn +
        postalCodeData.address?.ctyDstKn +
        postalCodeData.address?.lrgKn;
      setAddressInfoData({
        ...addressInfoData,
        postalCode: postalCodeData.address?.postalCode,
        address1: Number.isNaN(address1) ? "" : address1,
        address1Kn: Number.isNaN(address1Kn) ? "" : address1Kn,
        prfCd: postalCodeData.address?.prfCd,
        ctyCd: postalCodeData.address?.ctyCd,
        lrgCd: postalCodeData.address?.lrgCd,
      });

      setIsAddressInputEditable(true);
    }
    //検索条件を初期化する
    setPostalCodeSearchInput("");
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [postalCodeData]);

  //更新の場合のデータをセット
  useEffect(() => {
    setAddressInfoData(props.addressData);
    setCorporateData(props.corporateData);
  }, [props.addressData, props.corporateData]);

  //事業者情報入力フォームの活性化フラグ
  const [isInputEditable, setIsInputEditable] = useState(false);

  //所在地情報入力フォームの活性化フラグ
  const [isAddressInputEditable, setIsAddressInputEditable] = useState(false);

  //所在地情報入力フォームの活性化フラグ
  const [isUpdatableAddressKana2, setIsUpdatableAddressKana2] = useState(true);

  //法人番号検索実施フラグ
  const [isCorporateNumberSearchClicked, setIsCorporateNumberSearchClicked] =
    useState(false);

  useEffect(() => {
    //更新の場合、活性化する
    if (!props.isRegister) {
      setIsInputEditable(true);
      setIsAddressInputEditable(true);
      setIsUpdatableAddressKana2(false);
    } else if (isCorporateNumberSearchClicked) {
      setIsInputEditable(true);
    }
  }, [props.isRegister, isCorporateNumberSearchClicked]);

  // 法人番号検索をクリックした時
  const onClickCorporateNumberSearch = (
    e: React.FormEvent<HTMLFormElement>
  ) => {
    e.preventDefault();

    // バリデーションチェック
    if (
      checkCorporateNumber(
        registerCorporateData.corporateNumber,
        registerCheckCorporateNumberErrorMsg
      )
    ) {
      setSearchInput(registerCorporateData.corporateNumber);
      setCorporateData({
        ...corporateData,
        corporateNumber: registerCorporateData.corporateNumber,
        companyName: "",
        companyNameKana: "",
      });
      setIsCorporateNumberSearchClicked(false);
    }
  };

  // 郵便番号検索をクリックした時
  const onClickPostalCodeSearch = (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();

    // バリデーションチェック
    if (
      checkPostalCode(
        registerAddressData.postalCode,
        registerCheckPostalCodeErrorMsg
      )
    ) {
      setPostalCodeSearchInput(registerAddressData.postalCode);
      // 郵便番号を除く画面表示を初期化
      setAddressInfoData({
        ...addressInfoData,
        postalCode: registerAddressData.postalCode,
        address1: "",
        address1Kn: "",
        address2: "",
        address2Kn: "",
        prfCd: "",
        ctyCd: "",
        lrgCd: "",
        chrCd: "",
      });
      // 非活性にする
      setIsAddressInputEditable(false);
      setIsUpdatableAddressKana2(true);
    }
  };

  return (
    <SpaceBetween size="l">
      <CorporateNumberSearchUI
        isInputEditable={isInputEditable}
        isUpdatable={props.isUpdatable}
        onClickCorporateNumberSearch={onClickCorporateNumberSearch}
        corporateData={corporateData}
        setCorporateData={setCorporateData}
        setIsFormEntered={props.setIsFormEntered}
        showCompanyCode={props.showCompanyCode}
        hasCreatedBy={props.hasCreatedBy}
        hasCreatedAt={props.hasCreatedAt}
      />
      <AddressUI
        isInputEditable={isAddressInputEditable}
        isUpdatable={props.isUpdatable}
        addressData={addressInfoData}
        setAddressData={setAddressInfoData}
        setIsFormEntered={props.setIsFormEntered}
        onClickPostalCodeSearch={onClickPostalCodeSearch}
        isUpdatableAddressKana2={isUpdatableAddressKana2}
      />
    </SpaceBetween>
  );
};
